import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ApiKeyService } from '../../services/ApiKeyService/api-key';
import { ApiKeyIssuedResponse, CreateApiKeyRequest } from '../../models/api-key.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';

@Component({
  selector: 'app-create-api-key-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    ErrorBanner,
  ],
  templateUrl: './create-api-key-dialog.html',
  styleUrl: './create-api-key-dialog.scss',
})
export class CreateApiKeyDialog {
  name = '';
  expiresInDays: number | null = null;

  isSubmitting = signal(false);
  errorMessage = signal('');
  issued = signal<ApiKeyIssuedResponse | null>(null);
  copied = signal(false);

  constructor(private dialogRef: MatDialogRef<CreateApiKeyDialog>, private apiKeyService: ApiKeyService) {}

  cancel(): void {
    this.dialogRef.close(!!this.issued());
  }

  submit(): void {
    if (!this.name.trim()) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set('');

    const request: CreateApiKeyRequest = { name: this.name };
    if (this.expiresInDays) {
      request.expiresInDays = this.expiresInDays;
    }

    this.apiKeyService.create(request).subscribe({
      next: (res) => {
        this.isSubmitting.set(false);
        this.issued.set(res.data);
        this.dialogRef.disableClose = true;
      },
      error: (err: HttpErrorResponse) => {
        this.isSubmitting.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to create API key.'));
      },
    });
  }

  async copySecret(): Promise<void> {
    const secret = this.issued()?.secret;
    if (!secret) {
      return;
    }
    await navigator.clipboard.writeText(secret);
    this.copied.set(true);
  }

  done(): void {
    this.dialogRef.close(true);
  }
}
